import { useCallback } from 'react';

import { Step, Workflow } from './Workflow';
import useFeature from 'hooks/useFeature';
import { useSelector } from 'react-redux';
import { selectInstantCredit } from 'state/instantCredit';
import { selectPrequalOffer } from 'state/prequalOffer';
import { selectExpandedPanel } from 'state/unifiedWidgets';
import { selectFinanceResult } from 'state/financeOffers';
import useQueryParameters from 'hooks/useQueryParameters';

const useUnifiedPrequalWorkflow = () => {
  const { hasExistingAccount } = useSelector(selectInstantCredit);
  const { status } = useSelector(selectFinanceResult) || {};
  const { prequalificationId, hasOffer, notQualified } = useSelector(
    selectPrequalOffer,
  );
  const showOfferPage =
    hasExistingAccount || hasOffer || notQualified || status === 'APPROVED';

  const { isEnabled } = useFeature();
  const { selectedCardKey } = useQueryParameters();
  const expandedPanel = useSelector(selectExpandedPanel);

  const getWorkflow = useCallback((): Workflow => {
    const cardSelectorCheck =
      isEnabled('cardSelector') &&
      !selectedCardKey &&
      // card selector is part of the workflow until bread option selected
      (expandedPanel !== 'splitpay' && expandedPanel !== 'installments');

    const applicationReviewCheck: boolean =
      !!notQualified || (!!prequalificationId && !!hasOffer);

    const stepsEnabled = {
      [Step.UNIFIED_PREQUAL_HOME]: true,
      [Step.UNIFIED_OFFER_INTRO]: true,
      [Step.CARD_SELECTOR]: cardSelectorCheck,
      [Step.UNIFIED_CONSUMER_SEARCH]: true,
      [Step.UNIFIED_OFFER_APPLICATION]: true,
      [Step.UNIFIED_PREQUAL_OFFER]: showOfferPage,
      [Step.SECOND_CARD_SELECTOR]: cardSelectorCheck,
      [Step.UNIFIED_APPLICATION_REVIEW]: applicationReviewCheck,
    };

    return {
      stepsEnabled: stepsEnabled,
      paths: {
        [Step.UNIFIED_PREQUAL_HOME]: '/unified/offer-intro',
        [Step.UNIFIED_OFFER_INTRO]: '/unified/prequal-start',
        [Step.CARD_SELECTOR]: '/card-selector',
        [Step.UNIFIED_CONSUMER_SEARCH]: '/unified/consumer-search',
        [Step.UNIFIED_OFFER_APPLICATION]: '/unified/offer-application',
        [Step.UNIFIED_PREQUAL_OFFER]: '/unified/prequal-offer',
        [Step.SECOND_CARD_SELECTOR]: '/card-selector-fallback',
        [Step.UNIFIED_APPLICATION_REVIEW]: notQualified
          ? '/unified/credit-card-offer/credit-review'
          : '/unified/credit-card-offer/prequal-review',
      },
      progressStartStep: Step.UNIFIED_CONSUMER_SEARCH,
      restartStep: Step.UNIFIED_PREQUAL_HOME,
      timeoutRestartPath: '/unified/offer-intro',
    };
  }, [
    isEnabled,
    selectedCardKey,
    expandedPanel,
    notQualified,
    prequalificationId,
    hasOffer,
    showOfferPage,
  ]);

  return getWorkflow;
};

export default useUnifiedPrequalWorkflow;
